import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where, doc, getDoc, limit, orderBy, updateDoc, onSnapshot, getCountFromServer } from "firebase/firestore";
import {
  Card, CircularProgress, Avatar, Box, Typography, Divider,
  Stack, Grid, IconButton, Button, Chip, Container
} from "@mui/material";
import {
  School, People, DirectionsBus, Hotel, ConfirmationNumber,
  ArrowForwardIos, AccountBalanceWallet, Speed, Assessment, Logout
} from "@mui/icons-material";
import { WhatsApp, LocalPostOffice } from "@mui/icons-material"; // Added for styling
import { LinearProgress } from "@mui/material";

const PRIMARY = "#0d9488";
const SECONDARY = "#64748b";
const BG = "#f1f5f9"; // Slightly darker background to make white cards pop
const DARK = "#0f172a";
const COLORS = ["#0d9488", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

function Dashboard() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState({ total: 0, sent: 0, remaining: 0 });
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestEnquiries, setGuestEnquiries] = useState([]);
  const [recentPasses, setRecentPasses] = useState([]);
  const [status, setStatus] = useState(null); // 'Yes' or 'No'
  const [updating, setUpdating] = useState(false);
  const [todayCollection, setTodayCollection] = useState(0);
  const [summary, setSummary] = useState({
    students: 0,
    teachers: 0,
    staff: 0,
    classes: 0,
    vehicles: 0,
    hostelers: 0,
    activePasses: 0
  });

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        setUserData(userDoc.exists() ? userDoc.data() : user);
      } else {
        navigate("/login");
      }
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!userData?.schoolId) return;

    const fetchData = async () => {
      try {
        const sid = userData.schoolId;
        const queries = [
          getDocs(query(collection(db, "guestEnquiries"), orderBy("createdAt", "desc"), limit(4))),
          getDocs(query(collection(db, "gate_passes"), where("schoolId", "==", sid), where("status", "==", "Pending")))
        ];

        const [sS, tS, stS, cS, vS, hS, gS, pS] = await Promise.all(queries);

        setSummary({
          hostelers: hS.size,
          activePasses: pS ? pS.size : 0
        });

        setGuestEnquiries(gS.docs.map(d => ({ id: d.id, ...d.data() })));
        setRecentPasses(pS.docs.slice(0, 4).map(d => ({ id: d.id, ...d.data() })));


      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };



    fetchData();
  }, [userData]);



  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', background: BG }}>
        <CircularProgress style={{ color: PRIMARY }} />
      </Box>
    );
  }


  return (
    <Box sx={{ background: BG, minHeight: "100vh" }}>
      <Grid >
        {/* Header Section */}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={2} spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={900} color={DARK} sx={{ letterSpacing: '-0.5px' }}>
              Dashboard
            </Typography>
          </Box>
        </Stack>

        {/* Top Statistics Cards */}
        <Grid className="row" spacing={3}>
          <Grid className="col-sm-6" item xs={12} md={6}>
            <Grid className="row">
             
              <Grid className="col-sm-12 mb-3">
                <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0', height: 380, display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" justifyContent="space-between" mb={3} alignItems="center">
                    <Typography variant="subtitle1" fontWeight={800} color={DARK}>Pending Passes</Typography>
                    <Chip label={`${summary.activePasses} New`} size="small" sx={{ bgcolor: '#fff7ed', color: '#c2410c', fontWeight: 700, border: '1px solid #ffedd5' }} />
                  </Stack>
                  <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    {recentPasses.length > 0 ? recentPasses.map((pass, i) => (
                      <Box key={pass.id} sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ width: 36, height: 36, bgcolor: '#f1f5f9', color: DARK, fontSize: 13, fontWeight: 700 }}>
                            {pass.name?.[0]}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" fontWeight={700} color={DARK}>{pass.name}</Typography>
                            <Typography variant="caption" color={SECONDARY}>{pass.reason}</Typography>
                          </Box>
                          <Typography variant="caption" fontWeight={800} sx={{ color: PRIMARY }}>{pass.status}</Typography>
                        </Stack>
                        {i !== recentPasses.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                      </Box>
                    )) : (
                      <Typography variant="body2" color={SECONDARY} textAlign="center" mt={4}>No pending requests</Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
            </Grid>

          </Grid>
          <Grid item xs={12} className="col-sm-6">
            <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0', height: '100%', bgcolor: '#fff' }}>
              <Stack direction="row" justifyContent="space-between" mb={3} alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight={800} color={DARK}>Visitor's Log</Typography>
                  <Typography variant="caption" color={SECONDARY}>Today's activity</Typography>
                </Box>
                <IconButton size="small" onClick={() => navigate('/guest-enquiry')} sx={{ bgcolor: '#f8fafc' }}>
                  <ArrowForwardIos sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>

              <Stack spacing={2}>
                {guestEnquiries.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 10, color: SECONDARY }}>
                    <Typography variant="body2">No visitors recorded today</Typography>
                  </Box>
                ) : (
                  guestEnquiries.map((guest) => (
                    <Box key={guest.id} sx={{ p: 1, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #f1f5f9' }}>
                      <Stack direction="row" spacing={2} mb={2}>
                        <Avatar src={guest.photo} variant="rounded" sx={{ width: 48, height: 48, borderRadius: 2 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={700} color={DARK}>{guest.guestName}</Typography>
                          <Typography variant="caption" color={SECONDARY}>{guest.contactNo}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Chip label={`In: ${guest.inTime}`} size="small" sx={{ height: 20, fontSize: '10px', fontWeight: 700, bgcolor: '#ccfbf1', color: '#0f766e' }} />
                        <Typography variant="caption" fontWeight={600} color={DARK}>{guest.visitingTarget}</Typography>
                      </Stack>
                    </Box>
                  ))
                )}
              </Stack>

              <Button
                fullWidth
                variant="text"
                sx={{ mt: 3, py: 1.5, borderRadius: 3, color: PRIMARY, fontWeight: 700, textTransform: 'none' }}
                onClick={() => navigate('/guest-enquiry')}
              >
                View Full Logs
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;